import { useCallback, useRef, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  type Connection,
  type Node,
  type Edge,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { nodeTypes } from '../components/nodes'
import { FlowToolbar } from '../components/FlowToolbar'
import { useEmailFlow, useActualizarFlujo, useImportarContactos, useEjecutarFlujo } from '../hooks/useEmailFlows'
import { useFlowLinks } from '../hooks/useFlowLinks'
import type { ContactoRow } from '../types'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

let nodeCounter = 0

function FlowBuilderInner() {
  const { id } = useParams<{ id: string }>()
  const flujoId = parseInt(id || '0')
  const { data: flujo, isLoading } = useEmailFlow(flujoId)
  useDocumentTitle(flujo ? `${flujo.nombre} — Editor` : 'Editor de Flujo')

  const actualizarFlujo = useActualizarFlujo(flujoId)
  const importarContactos = useImportarContactos(flujoId)
  const ejecutarFlujo = useEjecutarFlujo()
  const contactosRef = useRef<ContactoRow[]>([])

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [nombre, setNombre] = useState('')
  const { screenToFlowPosition } = useReactFlow()

  // Los callbacks (onChange/onContactosChange) no se persisten en el JSON;
  // se re-inyectan al crear un nodo y al cargar un flujo existente.
  const makeOnChange = useCallback(
    (nodeId: string) => (newData: object) =>
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n)),
      ),
    [setNodes],
  )

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    },
    [setNodes, setEdges],
  )

  const attachCallbacks = useCallback(
    (node: Node): Node => {
      if (node.type === 'envioEmail') {
        return {
          ...node,
          data: {
            ...node.data,
            onChange: makeOnChange(node.id),
            onDelete: () => deleteNode(node.id),
            onContactosChange: (rows: ContactoRow[]) => {
              contactosRef.current = rows
            },
          },
        }
      }
      if (node.type === 'condicion') {
        return {
          ...node,
          data: {
            ...node.data,
            onChange: makeOnChange(node.id),
            onDelete: () => deleteNode(node.id),
          },
        }
      }
      return node
    },
    [makeOnChange, deleteNode],
  )

  // Hidrata el canvas cuando llega el flujo (useNodesState solo toma el valor inicial
  // una vez; sin esto, un flujo guardado abriría vacío y "Guardar" lo borraría).
  useEffect(() => {
    if (!flujo) return
    setNodes((flujo.definicion_json?.nodes || []).map((n) => attachCallbacks(n as Node)))
    setEdges((flujo.definicion_json?.edges || []) as Edge[])
    setNombre(flujo.nombre || '')
  }, [flujo, attachCallbacks, setNodes, setEdges])

  function handleNombreBlur() {
    const limpio = nombre.trim()
    if (flujo && limpio && limpio !== flujo.nombre) {
      actualizarFlujo.mutate({ nombre: limpio })
    }
  }

  // Extrae links del HTML de los nodos de envío para el dropdown de condiciones
  const ultimoHtml = nodes
    .filter((n) => n.type === 'envioEmail')
    .map((n) => (n.data as { html?: string }).html || '')
    .join(' ')
  const linksDisponibles = useFlowLinks(ultimoHtml)

  // Mantiene links_disponibles al día en cada nodo de condición cuando el HTML
  // cambia (si no, el dropdown de "link específico" quedaría con los links viejos
  // o vacío si la condición se creó antes de escribir el HTML).
  useEffect(() => {
    setNodes((nds) => {
      let changed = false
      const next = nds.map((n) => {
        if (n.type !== 'condicion') return n
        const cur = (n.data as { links_disponibles?: string[] }).links_disponibles || []
        const igual =
          cur.length === linksDisponibles.length &&
          cur.every((u, i) => u === linksDisponibles[i])
        if (igual) return n
        changed = true
        return { ...n, data: { ...n.data, links_disponibles: linksDisponibles } }
      })
      return changed ? next : nds
    })
  }, [linksDisponibles, setNodes])

  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges],
  )

  function makeNodeId() {
    nodeCounter += 1
    return `node-${Date.now()}-${nodeCounter}`
  }

  function addEnvioNode() {
    const esInicial = !nodes.some((n) => n.type === 'envioEmail')
    const newNode = attachCallbacks({
      id: makeNodeId(),
      type: 'envioEmail',
      position: screenToFlowPosition({ x: 300, y: 200 }),
      data: {
        titulo: '',
        asunto: '',
        preencabezado: '',
        html: '',
        esNodoInicial: esInicial,
      },
    })
    setNodes((nds) => [...nds, newNode])
  }

  function addCondicionNode() {
    const newNode = attachCallbacks({
      id: makeNodeId(),
      type: 'condicion',
      position: screenToFlowPosition({ x: 300, y: 350 }),
      data: {
        condicion: 'abrió',
        esperar_dias: 1,
        links_disponibles: linksDisponibles,
      },
    })
    setNodes((nds) => [...nds, newNode])
  }

  // Quita los callbacks runtime antes de persistir (no son serializables).
  function stripCallbacks(nds: Node[]): Node[] {
    return nds.map((n) => {
      const { onChange: _o, onContactosChange: _c, onDelete: _d, ...rest } = n.data as Record<string, unknown>
      return { ...n, data: rest }
    })
  }

  async function handleSave() {
    const definicion_json = { nodes: stripCallbacks(nodes), edges }
    await actualizarFlujo.mutateAsync({ definicion_json })
    if (contactosRef.current.length > 0) {
      await importarContactos.mutateAsync(contactosRef.current)
      contactosRef.current = []
    }
  }

  async function handleExecute() {
    await handleSave()
    await ejecutarFlujo.mutateAsync(flujoId)
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <FlowToolbar
        nombre={nombre}
        onNombreChange={setNombre}
        onNombreBlur={handleNombreBlur}
        onAddEnvio={addEnvioNode}
        onAddCondicion={addCondicionNode}
        onSave={handleSave}
        onExecute={handleExecute}
        isSaving={actualizarFlujo.isPending}
        isExecuting={ejecutarFlujo.isPending}
      />
      <div className="flex-1 min-h-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls />
        </ReactFlow>
      </div>
    </div>
  )
}

export function FlowBuilderPage() {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner />
    </ReactFlowProvider>
  )
}
